import { Component, OnDestroy, OnInit } from '@angular/core';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  KeycloakRole,
  UserKeycloak,
  UserWithRolesDTO,
} from '../../shared/interfaces/keycloak/UserKeycloak';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GestionComptesService } from '../../shared/services/gestion-comptes.service';
import { catchError, finalize, of, Subject, takeUntil, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { SnackbarHelper } from 'src/app/shared/services/snackbar/snackbar.helper';
import { ConfirmationDialog } from 'src/app/shared/components/alert-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UserSessionService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-utilisateurs',
  standalone: true,
  imports: [
    MatTableModule,
    MatCheckboxModule,
    MatIcon,
    MatIconButton,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
  ],
  templateUrl: './utilisateurs.component.html',
  styleUrl: './utilisateurs.component.scss',
})
export class UtilisateursComponent implements OnInit, OnDestroy {
  private _destroying$ = new Subject<void>();

  private readonly KEY_ADMIN = 'adminCockpit';
  adminItems = [] as any[];

  displayedColumns: string[] = ['lastName', 'firstName', 'email', 'roles', 'action'];

  formgroup: FormGroup;

  dataSourceUtilisateurs = new MatTableDataSource<UserKeycloak>();
  currentEdit: UserKeycloak | null = null;
  userWithRolesDTO: UserWithRolesDTO[] = [];
  originalRoles: { [id: string]: string[] } = {};
  readonly ROLE_ACCUEIL = 'Accueil';
  readonly GROUP_PFA_TITULAIRE = 'pfa.titulaire';
  readonly GROUP_PFA_DSI = 'pfa.dsi';
  readonly GROUP_PFA_SIEGE = 'pfa.siege';
  readonly GROUP_PFA_ADHERENT = 'pfa.adherent';

  readonly GROUP_PFA_TITULAIRE_GPO = 'pfa-gpo.titulaire';
  readonly GROUP_PFA_RESPONSABLE_GPO = 'pfa-gpo.responsable';
  readonly GROUP_PFA_ADHERENT_GPO = 'pfa-gpo.adherent';
  activiteAdherent!: string;

  constructor(
    private gestionComptesService: GestionComptesService,
    private snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
    private userSessionService: UserSessionService,
  ) {
    this.formgroup = new FormGroup({});
  }

  ngOnInit(): void {
    this.gestionComptesService.idAdherent
      .pipe(
        takeUntil(this._destroying$),
        tap(idAdherent => {
          this.onAdherentChange(idAdherent);
        }),
      )
      .subscribe();
    this.adminItems = JSON.parse(localStorage[this.KEY_ADMIN] || '[]');

    this.activiteAdherent = this.userSessionService.getActivite() ?? '';
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  getStatut(enabled: boolean): string {
    return enabled ? 'Actif' : 'Inactif';
  }

  onAdherentChange(idAdherent: number) {
    if (idAdherent == 0) {
      return;
    }
    this.getUsersWithRoles(idAdherent);
  }

  /**
   * Fetches users with roles associated with a specific `idAdherent` and processes the data.
   *
   * This function calls the service `getUsersByIdAdherent` to retrieve users for a given `idAdherent`.
   * It filters the roles of each user to only include those starting with 'pfa', and stores the resulting list
   * of users with their roles in `dataSourceUtilisateurs`. The function also filters out disabled users.
   *
   * @param {number} idAdherent - The ID of the adherent whose associated users are to be fetched.
   *
   * @returns {void} This function does not return any value. It updates internal state (`dataSourceUtilisateurs`) instead.
   */
  getUsersWithRoles(idAdherent: number) {
    if (idAdherent != null) {
    // Call to the service
    this.gestionComptesService
      .getUsersByIdAdherent(idAdherent)
      .pipe(
        takeUntil(this._destroying$),
        tap((users: UserWithRolesDTO[]) => {
          this.userWithRolesDTO = users;
          let userKeycloaks: UserKeycloak[] = [];
          users.forEach((user: UserWithRolesDTO) => {
            if (user.enabled) {
              userKeycloaks.push({
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                roles: user.roles
                  .filter(role => role.name.startsWith('pfa'))
                  .map(role => role.name),
                groups: user.groups.map(group => group.name),
                enabled: user.enabled,
                isInactivated: false,
              });
            }
          });

          let filteredUsers: UserKeycloak[];

          if (this.activiteAdherent === 'gpo') {
            filteredUsers = userKeycloaks.filter(
              (user: UserKeycloak) =>
                !user?.groups.includes(this.GROUP_PFA_RESPONSABLE_GPO) &&
                !user?.groups.includes(this.GROUP_PFA_TITULAIRE_GPO) &&
                user?.groups.includes(this.GROUP_PFA_ADHERENT_GPO),
            );
          } else {
            filteredUsers = userKeycloaks.filter(
              (user: UserKeycloak) =>
                !user?.groups.includes(this.GROUP_PFA_TITULAIRE) &&
                !user?.groups.includes(this.GROUP_PFA_DSI) &&
                !user?.groups.includes(this.GROUP_PFA_SIEGE) &&
                user?.groups.includes(this.GROUP_PFA_ADHERENT),
            );
          }


          this.dataSourceUtilisateurs = new MatTableDataSource<UserKeycloak>(filteredUsers);
        }),
      )
      .subscribe();
      }
  }

  /**
   * Initiates the user edit process by copying the user's data and storing the original roles.
   *
   * This function sets up an editing session for the given user by copying the user's data into `currentEdit`.
   * It also stores a copy of the user's current roles in `originalRoles` to allow restoration if needed.
   *
   * @param {UserKeycloak} userToEdit - The user whose information is being edited. The user's roles are stored for potential restoration.
   *
   * @returns {void} This function does not return any value.
   */
  editUser(userToEdit: UserKeycloak) {
    this.currentEdit = { ...userToEdit };
    this.originalRoles[userToEdit.id] = [...userToEdit.roles];
  }

  /**
   * Saves the current user's edited roles.
   *
   * This method checks if there is a user currently being edited (`currentEdit`).
   * If so, it transforms the user's data into a `UserWithRolesDTO` and sends it to the
   * `updateUserRole` service method. After the update, it reloads the user list if successful.
   *
   * It handles the following scenarios:
   * - Automatically unsubscribes from the observable when the component is destroyed using `takeUntil`.
   * - Logs an error message if the update fails using `catchError`.
   * - Resets `currentEdit` whether the update succeeds or fails using `finalize`.
   */
  onSaveEdit() {
    if (this.currentEdit) {
      this.gestionComptesService
        .updateUserRole([this.transformToUserWithRolesDTO(this.currentEdit, this.userWithRolesDTO)])
        .pipe(
          takeUntil(this._destroying$), // Automatically complete the observable when the component is destroyed
          tap((data: any) => {
            this.snackbarService.show(
              "La modification d'utilisateurs a été effectuée avec succès",
              SnackbarHelper.snackbarPanelClassSuccess(),
            );
            if (this.currentEdit) {
              this.getUsersWithRoles(this.gestionComptesService.getId());
            }
          }),
          catchError(error => {
            console.error('Error while saving user roles:', error);
            this.snackbarService.show(
              "Une erreur est survenue lors de la modification d'utilisateur : " + error.message,
              SnackbarHelper.snackbarPanelClassError(),
            );
            return of(null); // Return an empty observable in case of error
          }),
          finalize(() => {
            this.currentEdit = null; // Reset `currentEdit` whether the operation succeeds or fails
          }),
        )
        .subscribe(); // Trigger the observable
    }
  }

  onInactivate(userToInactivate: UserKeycloak) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        message: `Valider la désactivation ? Attention, vous ne pourrez pas réactiver le compte vous-même.`,
        buttonText: {
          ok: 'Oui',
          cancel: 'Non',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        if (userToInactivate) {
          this.gestionComptesService
            .inactivateUser(userToInactivate.id)
            .pipe(
              takeUntil(this._destroying$), // Automatically complete the observable when the component is destroyed
              tap(() => {
                this.snackbarService.show(
                  "La désactivation de l'utilisateur a été effectuée avec succès",
                  SnackbarHelper.snackbarPanelClassSuccess(),
                );
                this.getUsersWithRoles(this.gestionComptesService.getId());
              }),
              catchError(error => {
                console.error('Error while inactivating user:', error);
                this.snackbarService.show(
                  "Une erreur est survenue lors de la désactivation de l'utilisateur : " +
                    error.message,
                  SnackbarHelper.snackbarPanelClassError(),
                );
                return of(null); // Return an empty observable in case of error
              }),
              finalize(() => {
                this.currentEdit = null; // Reset `currentEdit` whether the operation succeeds or fails
              }),
            )
            .subscribe(); // Trigger the observable
        }
      }
    });
  }

  /**
   * Cancels the edit action and restores the original roles of the user.
   *
   * If there is an active editing session (`currentEdit`), this function will restore the user's roles
   * to their original state by copying them from `originalRoles`. It will also update the corresponding
   * user in `dataSourceUtilisateurs.data` with the original roles.
   *
   * After restoring the roles, the `currentEdit` is set to `null`, ending the editing session.
   *
   * @returns {void} This function does not return any value.
   */
  onCancelEdit() {
    if (this.currentEdit && this.originalRoles[this.currentEdit.id]) {
      const originalRoles = this.originalRoles[this.currentEdit.id];
      this.currentEdit.roles = [...originalRoles];

      const editedUser = this.dataSourceUtilisateurs.data.find(
        user => user.id === this.currentEdit?.id,
      );
      if (editedUser) {
        editedUser.roles = [...originalRoles];
      }
    }

    this.currentEdit = null;
  }

  /**
   * Adds or removes a role from a user based on the checkbox state.
   *
   * If the user is `null`, the function does nothing. If `isChecked` is `true`,
   * the specified role is added to the user's role list if it's not already included.
   * If `isChecked` is `false`, the role is removed from the user's role list if it's present.
   *
   * @param {UserKeycloak | null} user - The user to whom the role will be added or removed. If null, no action is performed.
   * @param {string} role - The role to be added or removed from the user.
   * @param {boolean} isChecked - The checkbox state. If `true`, the role is added; if `false`, it is removed.
   *
   * @returns {void} This function does not return any value.
   */
  toggleRole(user: UserKeycloak | null, role: string, isChecked: boolean) {
    if (!user) {
      return;
    }
    if (isChecked) {
      if (!user.roles.includes(role)) {
        user.roles.push(role);
      }
    } else {
      user.roles = user.roles.filter(r => r !== role);
    }
  }

  /**
   * Transforms a UserKeycloak object into a UserWithRolesDTO object.
   *
   * This method maps the user's roles by comparing the roles provided in
   * the user object with detailed role information from userWithRolesDTO.
   * If a role is not found in userWithRolesDTO, a default role object with minimal information is created.
   *
   * @param {UserKeycloak} user - The user object from Keycloak containing basic user details and roles.
   * @param {UserWithRolesDTO[]} userWithRolesDTO - An array of UserWithRolesDTO providing detailed role information.
   * @returns {UserWithRolesDTO} A UserWithRolesDTO object with the user's details and enriched role information.
   */
  transformToUserWithRolesDTO(
    user: UserKeycloak,
    userWithRolesDTO: UserWithRolesDTO[],
  ): UserWithRolesDTO {
    const roles: KeycloakRole[] = user.roles.map(roleName => {
      const roleDetail = userWithRolesDTO.flatMap(u => u.roles).find(r => r.name === roleName);

      if (roleDetail) {
        return {
          id: roleDetail.id,
          name: roleDetail.name,
          composite: roleDetail.composite,
          clientRole: roleDetail.clientRole,
          containerId: roleDetail.containerId,
        };
      } else {
        console.warn(`Role ${roleName} not found in userWithRolesDTO`);
        return {
          id: '',
          name: roleName,
          composite: false,
          clientRole: false,
        };
      }
    });

    return {
      id: user.id,
      username: user.email.split('@')[0],
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      createdTimestamp: Date.now(),
      enabled: user.enabled,
      roles: roles,
      groups: [],
      poste: '',
      tempPassword: '',
      isInactivated: false,
      existingEmail: false,
    };
  }
}
